"use client";

import {
  Award,
  Bookmark,
  ChefHat,
  Heart,
  Share,
  Utensils,
  Wine,
} from "lucide-react";
import Image from "next/image";
import type { InstagramPost } from "@/types";

interface InstagramPostCardProps {
  post: InstagramPost;
}

const iconMap = {
  "chef-hat": ChefHat,
  award: Award,
  wine: Wine,
  utensils: Utensils,
  heart: Heart,
  bookmark: Bookmark,
  share: Share,
};

export default function InstagramPostCard({ post }: InstagramPostCardProps) {
  const IconComponent = iconMap[post.icon];
  const hasCaption = post.caption.length > 0;
  const hasTimestamp = post.timestamp.length > 0;

  return (
    <div className="instagram-post break-inside-avoid bg-white/20 border border-white/30 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 group cursor-pointer mb-4">
      <div className="relative">
        <Image
          src={post.image}
          alt={post.alt}
          width={800}
          height={600}
          className={`w-full ${post.heightClass} object-cover group-hover:scale-105 transition-transform duration-700`}
          unoptimized
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />

        {/* Hover Icon */}
        <div className="absolute top-3 right-3 bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <IconComponent
            className={`w-4 h-4 ${post.icon === "heart" ? "text-red-500" : "text-black"}`}
          />
        </div>

        {/* Likes Badge for Square Posts */}
        {post.aspectRatio === "square" && (
          <div className="absolute bottom-3 left-3 bg-white/90 rounded-full px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="text-xs font-medium text-black">
              {post.likes} likes
            </span>
          </div>
        )}
      </div>

      {/* Post Content */}
      {(hasCaption || hasTimestamp) && (
        <div className="p-4">
          {/* Username Row */}
          {hasCaption && (
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-black/10 rounded-full flex items-center justify-center">
                <IconComponent className="w-4 h-4 text-black" />
              </div>
              <span className="font-medium text-sm text-black">
                {post.username}
              </span>
            </div>
          )}

          {/* Caption */}
          {hasCaption && (
            <p className="text-sm text-black/70 mb-2">{post.caption}</p>
          )}

          {/* Timestamp and Likes */}
          {(hasTimestamp || hasCaption) && (
            <div className="flex items-center gap-4 text-xs text-black/60">
              {hasTimestamp && <span>{post.timestamp}</span>}
              <span>{post.likes} likes</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
